import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getEncuesta,
  toggleMultiplesRespuestas,
  getFormularioEncuesta,
  crearPasoEncuesta, editarPasoEncuesta, eliminarPasoEncuesta, reordenarPasosEncuesta,
  crearCampoEncuesta, editarCampoEncuesta, eliminarCampoEncuesta, reordenarCamposEncuesta,
  getAnaliticasEncuesta,
  exportarRespuestasEncuesta,
} from '../api/muestrasApi'
import type { PasoCreatePayload, CampoCreatePayload } from '../types'

export const encuestasKeys = {
  all: ['muestras-encuestas'] as const,
  config: (campaniaId: number) => [...encuestasKeys.all, 'config', campaniaId] as const,
  formBuilder: (campaniaId: number) => [...encuestasKeys.all, 'form-builder', campaniaId] as const,
  analiticas: (campaniaId: number) => [...encuestasKeys.all, 'analiticas', campaniaId] as const,
}

export function useEncuesta(campaniaId: number) {
  return useQuery({
    queryKey: encuestasKeys.config(campaniaId),
    queryFn: () => getEncuesta(campaniaId),
    enabled: campaniaId > 0,
  })
}

export function useToggleMultiplesRespuestas(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => toggleMultiplesRespuestas(campaniaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: encuestasKeys.config(campaniaId) })
    },
  })
}

export function useFormularioEncuesta(campaniaId: number) {
  return useQuery({
    queryKey: encuestasKeys.formBuilder(campaniaId),
    queryFn: () => getFormularioEncuesta(campaniaId),
    enabled: campaniaId > 0,
  })
}

// CRUD pasos/campos encuesta
export function useCrearPasoEncuesta(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: PasoCreatePayload) => crearPasoEncuesta(campaniaId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: encuestasKeys.formBuilder(campaniaId) })
      toast.success('Paso creado')
    },
  })
}

export function useEditarPasoEncuesta(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ pasoId, payload }: { pasoId: number; payload: Partial<PasoCreatePayload> }) =>
      editarPasoEncuesta(campaniaId, pasoId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: encuestasKeys.formBuilder(campaniaId) }),
  })
}

export function useEliminarPasoEncuesta(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (pasoId: number) => eliminarPasoEncuesta(campaniaId, pasoId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: encuestasKeys.formBuilder(campaniaId) }),
  })
}

export function useReordenarPasosEncuesta(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orden: number[]) => reordenarPasosEncuesta(campaniaId, orden),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: encuestasKeys.formBuilder(campaniaId) }),
  })
}

export function useCrearCampoEncuesta(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CampoCreatePayload) => crearCampoEncuesta(campaniaId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: encuestasKeys.formBuilder(campaniaId) }),
  })
}

export function useEditarCampoEncuesta(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ campoId, payload }: { campoId: number; payload: Partial<CampoCreatePayload> }) =>
      editarCampoEncuesta(campaniaId, campoId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: encuestasKeys.formBuilder(campaniaId) }),
  })
}

export function useEliminarCampoEncuesta(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (campoId: number) => eliminarCampoEncuesta(campaniaId, campoId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: encuestasKeys.formBuilder(campaniaId) }),
  })
}

export function useReordenarCamposEncuesta(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orden: number[]) => reordenarCamposEncuesta(campaniaId, orden),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: encuestasKeys.formBuilder(campaniaId) }),
  })
}

export function useAnaliticasEncuesta(campaniaId: number) {
  return useQuery({
    queryKey: encuestasKeys.analiticas(campaniaId),
    queryFn: () => getAnaliticasEncuesta(campaniaId),
    enabled: campaniaId > 0,
  })
}

export function useExportarRespuestas() {
  return useMutation({
    mutationFn: (campaniaId: number) => exportarRespuestasEncuesta(campaniaId),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'respuestas_encuesta.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    },
    onError: () => toast.error('Error al exportar respuestas'),
  })
}
